<script setup lang="ts">
  import { getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSettingsCard from '@/components/pos-v2/ui/PosUiSettingsCard.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import type { ICatalogStatus } from '@/composition/design/use-design-page';
  import type { IStylePresetOption } from '@/domain/design/style-catalog';

  /**
   * «Estilo» (Storefront V2 ADM-3): the storefront's styles as a radio list.
   * The list comes from the storefront's catalog; while it loads the card
   * shows skeleton rows, and when it fails a «Reintentar» button. A saved
   * style the catalog lacks leaves every row unchecked.
   */
  defineProps<{
    value: string;
    presets: IStylePresetOption[];
    status: ICatalogStatus;
  }>();
  const emit = defineEmits<{
    (e: 'input', id: string): void;
    (e: 'retry'): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string) => String(proxy.$t(key));
  const SKELETON_ROWS = [1, 2, 3, 4];
</script>

<template>
  <PosUiSettingsCard
    class="v2-design-style-card"
    :title="t('design.style.title')"
    :description="t('design.style.description')"
    flush
  >
    <div v-if="status === 'loading'" class="style-loading" aria-hidden="true">
      <div v-for="row in SKELETON_ROWS" :key="row" class="loading-row">
        <PosUiSkeleton class="loading-bar" />
      </div>
    </div>
    <div v-else-if="status === 'error'" class="style-error">
      <p class="error-text">{{ t('design.style.loadError') }}</p>
      <PosUiButton
        color="neutral"
        variant="outline"
        icon="refresh"
        @click="emit('retry')"
      >
        {{ t('design.retry') }}
      </PosUiButton>
    </div>
    <div
      v-else
      class="style-options"
      role="radiogroup"
      :aria-label="t('design.style.title')"
    >
      <button
        v-for="preset in presets"
        :key="preset.id"
        type="button"
        role="radio"
        class="style-option"
        :class="{ 'is-selected': preset.id === value }"
        :aria-checked="preset.id === value ? 'true' : 'false'"
        :data-preset="preset.id"
        @click="emit('input', preset.id)"
      >
        <span class="option-text">
          <span class="option-name">{{ preset.name }}</span>
          <span v-if="preset.hint" class="option-hint">{{ preset.hint }}</span>
        </span>
        <PosUiIcon
          class="option-mark"
          :name="preset.id === value ? 'check-circle' : 'circle-outline'"
          :size="22"
        />
      </button>
    </div>
  </PosUiSettingsCard>
</template>

<style scoped lang="scss">
  .v2-design-style-card {
    .style-loading {
      @apply tw-py-1;

      .loading-row {
        @apply tw-flex tw-h-14 tw-items-center tw-px-5 md:tw-px-6;
      }

      .loading-bar {
        @apply tw-h-8 tw-w-full;
      }
    }

    .style-error {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-px-5 tw-py-4 md:tw-px-6;

      .error-text {
        @apply tw-my-1 tw-mr-3 tw-text-sm tw-text-neutral-700;
      }
    }

    .style-option {
      @apply tw-flex tw-min-h-[56px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-border-0 tw-border-solid tw-border-neutral-200 tw-bg-transparent tw-px-5 tw-py-2 tw-text-left md:tw-px-6;

      & + .style-option {
        @apply tw-border-t;
      }

      &:last-child {
        @apply tw-rounded-b-xl;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
      }

      .option-text {
        @apply tw-mr-3 tw-flex tw-min-w-0 tw-flex-col;
      }

      .option-name {
        @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
      }

      .option-hint {
        @apply tw-mt-0.5 tw-text-xs tw-text-neutral-500;
      }

      .option-mark {
        @apply tw-flex-none tw-text-neutral-300;
      }

      &.is-selected .option-mark {
        @apply tw-text-neutral-900;
      }
    }

    @media (hover: hover) {
      .style-option:hover {
        @apply tw-bg-neutral-50;
      }
    }
  }
</style>
